import React, { useState } from 'react';
import styled from 'styled-components';
import { FileText, Target, List, ChevronDown, Bot, Loader2 } from 'lucide-react';
import { theme } from '../styles/theme';
import { Question, EvaluationCriteria, ExtractionMethod } from '../types';
import QuestionCard from './QuestionCard';
import EvaluationCriteriaGrid from './EvaluationCriteriaGrid';
import RequirementsSummary from './RequirementsSummary';
import ExtractionVisualization from './ExtractionVisualization';

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  margin-top: ${theme.spacing[4]};
`;

const Section = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  transition: all ${theme.transitions.base};

  &:hover {
    border-color: ${theme.colors.borderHover};
  }
`;

const SectionHeader = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  background: linear-gradient(135deg, ${theme.colors.backgroundSecondary}, ${theme.colors.surface});
  border-bottom: ${({ $collapsed }) => $collapsed ? 'none' : `1px solid ${theme.colors.border}`};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    background: linear-gradient(135deg, ${theme.colors.surfaceHover}, ${theme.colors.backgroundSecondary});
  }
`;

const SectionTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const SectionIcon = styled.div<{ $variant: 'questions' | 'criteria' | 'summary' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  color: white;
  
  ${({ $variant }) => {
    switch ($variant) {
      case 'questions':
        return `background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.primary[600]});`;
      case 'criteria':
        return `background: linear-gradient(135deg, ${theme.colors.accent[500]}, ${theme.colors.accent[600]});`;
      case 'summary':
        return `background: linear-gradient(135deg, ${theme.colors.success[500]}, ${theme.colors.success[600]});`;
      default:
        return `background: ${theme.colors.primary[500]};`;
    }
  }}
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.lg};
`;

const SectionSubtitle = styled.p`
  margin: ${theme.spacing[1]} 0 0 0;
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
`;

const SectionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const CountBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${theme.colors.primary[100]};
  color: ${theme.colors.primary[700]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const CollapseButton = styled.button<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  transform: ${({ $collapsed }) => $collapsed ? 'rotate(-90deg)' : 'rotate(0deg)'};

  &:hover {
    background: ${theme.colors.primary[100]};
    border-color: ${theme.colors.primary[400]};
    color: ${theme.colors.primary[600]};
  }
`;

const SectionContent = styled.div<{ $collapsed: boolean }>`
  max-height: ${({ $collapsed }) => $collapsed ? '0' : 'none'};
  overflow: hidden;
  transition: all ${theme.transitions.base};
`;

const SectionBody = styled.div`
  padding: ${theme.spacing[5]};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[8]};
  text-align: center;
  color: ${theme.colors.textMuted};
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.borderRadius.lg};
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing[3]};
  color: ${theme.colors.primary[500]};
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
`;

const InfoBanner = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.accent[50]});
  border: 1px solid ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  margin-bottom: ${theme.spacing[6]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const InfoIconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-shrink: 0;
`;

const InfoIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;


const InfoContent = styled.div`
  flex: 1;
`;

const InfoTitle = styled.h4`
  margin: 0 0 ${theme.spacing[2]} 0;
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const InfoDescription = styled.p`
  margin: 0;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const StreamingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.primary[100]});
  border: 1px solid ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.primary[700]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  margin-bottom: ${theme.spacing[3]};
`;

const StreamingIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${theme.colors.primary[500]};
  color: white;
`;

const ProgressBadge = styled.div<{ variant: 'streaming' | 'complete' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  
  ${({ variant }) => {
    switch (variant) {
      case 'streaming':
        return `
          background: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[700]};
          border: 1px solid ${theme.colors.primary[300]};
        `;
      case 'complete':
        return `
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
          border: 1px solid ${theme.colors.success[500]};
        `;
      default:
        return '';
    }
  }}
`;

const ViewAllButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  margin-top: ${theme.spacing[4]};
  background: linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.primary[100]});
  border: 1px solid ${theme.colors.primary[300]};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.primary[700]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    background: linear-gradient(135deg, ${theme.colors.primary[100]}, ${theme.colors.primary[200]});
    border-color: ${theme.colors.primary[400]};
    color: ${theme.colors.primary[800]};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

interface DocumentProcessingResultsProps {
  questions?: Question[];
  evaluationCriteria?: EvaluationCriteria[];
  requirementsSummary?: string;
  responses?: { [questionId: string]: string };
  isStreaming?: boolean;
  streamingProgress?: {
    questionsCount: number;
    criteriaCount: number;
    currentAction: string;
  };
  // LangExtract Source Provenance Props
  extractionVisualizationHtml?: string | null;
  extractionMethod?: ExtractionMethod;
  detectedTenderType?: 'rfp' | 'itt' | 'rfq' | null;
}

const DocumentProcessingResults: React.FC<DocumentProcessingResultsProps> = ({
  questions = [],
  evaluationCriteria = [],
  requirementsSummary,
  responses = {},
  isStreaming = false,
  streamingProgress,
  extractionVisualizationHtml,
  extractionMethod,
  detectedTenderType
}) => {
  const [sectionsCollapsed, setSectionsCollapsed] = useState({
    questions: false,
    criteria: false,
    summary: false
  });

  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [showAllCriteria, setShowAllCriteria] = useState(false);

  const toggleSection = (section: 'questions' | 'criteria' | 'summary') => {
    setSectionsCollapsed(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Helper function to display question count with selection metadata
  const getQuestionCountText = (questions: Question[]) => {
    const totalCount = questions.length;

    // Check if backend provided selection data
    const hasSelectionData = questions.some(q => q.selection_score !== undefined);

    if (hasSelectionData) {
      // Count selected questions (selected !== false means it's either selected or undefined)
      const selectedCount = questions.filter(q => q.selected !== false).length;

      if (selectedCount < totalCount) {
        return `${selectedCount} Selected (from ${totalCount} extracted)`;
      }
    }

    // Fallback to simple count
    return `${totalCount} ${totalCount === 1 ? 'Question' : 'Questions'}`;
  };

  const hasAnyResults = questions.length > 0 || evaluationCriteria.length > 0 || requirementsSummary;

  // Show empty state only if not streaming and no results
  if (!hasAnyResults && !isStreaming) {
    return (
      <ResultsContainer>
        <EmptyState>
          <EmptyIcon>
            <FileText size={24} />
          </EmptyIcon>
          <EmptyText>
            Document processing results will appear here as analysis progresses...
          </EmptyText>
        </EmptyState>
      </ResultsContainer>
    );
  }

  return (
    <ResultsContainer>
      {/* Streaming Indicator - Always show if we have no data yet */}
      {!hasAnyResults && (
        <StreamingIndicator>
          <StreamingIcon>
            <Loader2 size={12} className="spin" />
          </StreamingIcon>
          <div>
            <strong>Processing Document:</strong> {streamingProgress?.currentAction || 'Analyzing tender document...'}
          </div>
        </StreamingIndicator>
      )}

      {/* Information Banner */}
      <InfoBanner>
        <InfoIconContainer>
          <InfoIcon>
            <Bot size={16} />
          </InfoIcon>
        </InfoIconContainer>
        <InfoContent>
          <InfoTitle>
            Powered by AI & OpenTender Platform
          </InfoTitle>
          <InfoDescription>
            Open Tender combines advanced artificial intelligence with the comprehensive OpenTender platform 
            to deliver intelligent document analysis, pattern recognition, and optimized response generation. 
            Our dual-engine approach ensures accurate extraction and professional-grade tender responses.
          </InfoDescription>
        </InfoContent>
      </InfoBanner>

      {/* Questions Section */}
      <Section>
        <SectionHeader 
          $collapsed={sectionsCollapsed.questions}
          onClick={() => toggleSection('questions')}
        >
          <SectionTitleGroup>
            <SectionIcon $variant="questions">
              <FileText size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>Questions & Responses</SectionTitle>
              <SectionSubtitle>
                Extracted questions from the tender document
              </SectionSubtitle>
            </div>
          </SectionTitleGroup>
          
          <SectionMeta>
            {questions.length === 0 && (
              <ProgressBadge variant="streaming">
                <Loader2 size={12} className="spin" />
                Processing ({streamingProgress?.questionsCount || 0} found)
              </ProgressBadge>
            )}
            <CountBadge>
              <List size={14} />
              {getQuestionCountText(questions)}
            </CountBadge>
            <CollapseButton $collapsed={sectionsCollapsed.questions}>
              <ChevronDown size={16} />
            </CollapseButton>
          </SectionMeta>
        </SectionHeader>
        
        <SectionContent $collapsed={sectionsCollapsed.questions}>
          <SectionBody>
            {questions.length > 0 ? (
              <>
                {(showAllQuestions ? questions : questions.slice(0, 10)).map((question, index) => (
                  <QuestionCard
                    key={`question-${index}-${question.id || 'no-id'}`}
                    question={question}
                    response={responses[question.id]}
                    index={index}
                  />
                ))}
                {questions.length > 10 && !showAllQuestions && (
                  <ViewAllButton onClick={() => setShowAllQuestions(true)}>
                    <List size={16} />
                    View All {questions.length} Questions
                  </ViewAllButton>
                )}
                {showAllQuestions && questions.length > 10 && (
                  <ViewAllButton onClick={() => setShowAllQuestions(false)}>
                    <ChevronDown size={16} />
                    Show Less
                  </ViewAllButton>
                )}
              </>
            ) : (
              <EmptyState>
                <EmptyIcon>
                  <FileText size={24} />
                </EmptyIcon>
                <EmptyText>No questions extracted yet...</EmptyText>
              </EmptyState>
            )}
          </SectionBody>
        </SectionContent>
      </Section>

      {/* Source-Grounded Extraction Visualization (LangExtract) */}
      {extractionMethod === 'langextract' && (
        <ExtractionVisualization
          htmlContent={extractionVisualizationHtml}
          extractionCount={questions.length + evaluationCriteria.length}
        />
      )}

      {/* Evaluation Criteria Section */}
      <Section>
        <SectionHeader
          $collapsed={sectionsCollapsed.criteria}
          onClick={() => toggleSection('criteria')}
        >
          <SectionTitleGroup>
            <SectionIcon $variant="criteria">
              <Target size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>Evaluation Criteria</SectionTitle>
              <SectionSubtitle>
                Scoring criteria and weightings for the tender
              </SectionSubtitle>
            </div>
          </SectionTitleGroup>
          
          <SectionMeta>
            {evaluationCriteria.length === 0 && (
              <ProgressBadge variant="streaming">
                <Loader2 size={12} className="spin" />
                Processing ({streamingProgress?.criteriaCount || 0} found)
              </ProgressBadge>
            )}
            <CountBadge>
              <Target size={14} />
              {evaluationCriteria.length} {evaluationCriteria.length === 1 ? 'Criterion' : 'Criteria'}
            </CountBadge>
            <CollapseButton $collapsed={sectionsCollapsed.criteria}>
              <ChevronDown size={16} />
            </CollapseButton>
          </SectionMeta>
        </SectionHeader>
        
        <SectionContent $collapsed={sectionsCollapsed.criteria}>
          <SectionBody>
            {evaluationCriteria.length > 0 ? (
              <>
                <EvaluationCriteriaGrid 
                  criteria={showAllCriteria ? evaluationCriteria : evaluationCriteria.slice(0, 6)} 
                />
                {evaluationCriteria.length > 6 && !showAllCriteria && (
                  <ViewAllButton onClick={() => setShowAllCriteria(true)}>
                    <Target size={16} />
                    View All {evaluationCriteria.length} Criteria
                  </ViewAllButton>
                )}
                {showAllCriteria && evaluationCriteria.length > 6 && (
                  <ViewAllButton onClick={() => setShowAllCriteria(false)}>
                    <ChevronDown size={16} />
                    Show Less
                  </ViewAllButton>
                )}
              </>
            ) : (
              <EvaluationCriteriaGrid criteria={evaluationCriteria} />
            )}
          </SectionBody>
        </SectionContent>
      </Section>

      {/* Requirements Summary Section */}
      <Section>
        <SectionHeader 
          $collapsed={sectionsCollapsed.summary}
          onClick={() => toggleSection('summary')}
        >
          <SectionTitleGroup>
            <SectionIcon $variant="summary">
              <List size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>Requirements Summary</SectionTitle>
              <SectionSubtitle>
                Comprehensive summary of tender requirements
              </SectionSubtitle>
            </div>
          </SectionTitleGroup>
          
          <SectionMeta>
            <CollapseButton $collapsed={sectionsCollapsed.summary}>
              <ChevronDown size={16} />
            </CollapseButton>
          </SectionMeta>
        </SectionHeader>
        
        <SectionContent $collapsed={sectionsCollapsed.summary}>
          <SectionBody>
            <RequirementsSummary summary={requirementsSummary} />
          </SectionBody>
        </SectionContent>
      </Section>
    </ResultsContainer>
  );
};

export default DocumentProcessingResults;